import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheckCircle, faClock, faTimesCircle, faBan } from '@fortawesome/free-solid-svg-icons';
import type { HttpMethod, StatusKey } from './types';

const METHOD_COLORS: Record<string, string> = {
  GET: 'bg-sky-500/15 text-sky-400 border border-sky-500/20',
  POST: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20',
  PUT: 'bg-amber-500/15 text-amber-400 border border-amber-500/20',
  DELETE: 'bg-rose-500/15 text-rose-400 border border-rose-500/20',
  PATCH: 'bg-violet-500/15 text-violet-400 border border-violet-500/20',
};

export const MethodBadge: React.FC<{ method: HttpMethod }> = ({ method }) => (
  <span className={`api-docs-method-badge ${METHOD_COLORS[method]}`}>{method}</span>
);

/** ชื่อ field ในตาราง */
export const FieldCode: React.FC<{ children: string }> = ({ children }) => (
  <code className="text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/30 px-1.5 py-0.5 rounded text-xs font-mono font-medium">{children}</code>
);

/** ชนิดข้อมูลในตาราง (number / string / object ...) */
export const TypeText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="text-gray-500 dark:text-slate-400 font-mono text-xs">{children}</span>
);

export const Required: React.FC = () => (
  <span className="px-2 py-0.5 bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 rounded-full text-xs font-semibold">Required</span>
);

export const Optional: React.FC = () => (
  <span className="px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400 rounded-full text-xs font-medium">Optional</span>
);

export const StatusPill: React.FC<{ color: string; icon: IconDefinition; children: string }> = ({ color, icon, children }) => (
  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${color}`}>
    <FontAwesomeIcon icon={icon} className="text-[10px]" /> {children}
  </span>
);

/** สี/ไอคอนมาตรฐานของแต่ละสถานะ — ใช้ร่วมกันทุก section */
export const STATUS_PRESETS: Record<StatusKey, { color: string; icon: IconDefinition }> = {
  pending: { color: 'bg-amber-100 text-amber-700', icon: faClock },
  paid: { color: 'bg-emerald-100 text-emerald-700', icon: faCheckCircle },
  expired: { color: 'bg-gray-100 text-gray-600', icon: faTimesCircle },
  cancelled: { color: 'bg-rose-100 text-rose-700', icon: faBan },
  approved: { color: 'bg-sky-100 text-sky-700', icon: faCheckCircle },
  completed: { color: 'bg-emerald-100 text-emerald-700', icon: faCheckCircle },
  rejected: { color: 'bg-rose-100 text-rose-700', icon: faBan },
};

export const StatusValues: React.FC<{ values: StatusKey[] }> = ({ values }) => (
  <div className="flex flex-wrap gap-2">
    {values.map(key => (
      <StatusPill key={key} color={STATUS_PRESETS[key].color} icon={STATUS_PRESETS[key].icon}>{key}</StatusPill>
    ))}
  </div>
);
